import { memo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Mic, Loader2, X } from "lucide-react";
import { useVoiceAssistant } from "@/hooks/useVoiceAssistant";
import { cn } from "@/lib/utils";
import aiDoctorAvatar from "@/assets/ai-doctor-avatar.png";
import { AIChatDialog } from "./AIChatDialog";

interface AIAgentCardProps {
  /** Optional context for the AI (e.g., treatment name) */
  context?: string;
  /** Card title - defaults to "Talk to Dr. Maya" */
  title?: string;
  /** Card description */
  description?: string;
  /** Quick prompts to show */
  quickPrompts?: string[];
  /** Show message button */
  showMessageButton?: boolean;
  /** Additional className */
  className?: string;
  /** Compact mode - hides quick prompts */
  compact?: boolean;
}

const defaultPrompts = [
  "What's the success rate?",
  "How long is recovery?",
  "Total cost estimate?",
];

export const AIAgentCard = memo(function AIAgentCard({
  context,
  title = "Talk to Dr. Maya",
  description,
  quickPrompts = defaultPrompts,
  showMessageButton = true,
  className,
  compact = false,
}: AIAgentCardProps) {
  const { isConnecting, isActive, toggleConversation, assistantState } = useVoiceAssistant();

  const defaultDescription = context
    ? `Get personalized answers about ${context}, costs, and options.`
    : "Get personalized answers about treatments, hospitals, and pricing.";

  return (
    <Card className={cn(
      "bg-gradient-to-br from-primary/5 via-accent/5 to-primary/5 border-primary/20 overflow-hidden w-full max-w-full min-w-0",
      className
    )}>
      <CardContent className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
          {/* Avatar */}
          <div className="relative shrink-0 self-center sm:self-start">
            <div className={cn(
              "w-14 h-14 sm:w-16 sm:h-16 rounded-full overflow-hidden border-2 shadow-lg transition-colors",
              isActive ? "border-accent" : "border-primary/30"
            )}>
              <img 
                width={80}
                height={80}
                src={aiDoctorAvatar} 
                alt="Dr. Maya - AI Assistant" 
                className="w-full h-full object-cover"
              />
            </div>
            <div className={cn(
              "absolute -bottom-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center border-2 border-card transition-colors",
              isActive ? "bg-accent" : "bg-accent"
            )}>
              <Mic className="w-2 h-2 sm:w-2.5 sm:h-2.5 text-accent-foreground" />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 text-center sm:text-left">
            <h2 className="font-display font-semibold text-secondary mb-1 text-base sm:text-lg">
              {title}
            </h2>
            <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4 line-clamp-2">
              {description || defaultDescription}
            </p>

            <div className="flex flex-wrap gap-2 justify-center sm:justify-start max-w-full">
              <Button 
                onClick={toggleConversation}
                disabled={isConnecting}
                variant={isActive ? "destructive" : "default"}
                className="rounded-full gap-2 text-xs sm:text-sm"
                size="sm"
              >
                {isConnecting ? (
                  <>
                    <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 animate-spin" />
                    <span>Calling...</span>
                  </>
                ) : isActive ? (
                  <>
                    <X className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span>End Call</span>
                  </>
                ) : (
                  <>
                    <Mic className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span>Start Voice Call</span>
                  </>
                )}
              </Button>
              
              {showMessageButton && !isActive && (
                <AIChatDialog context={context} />
              )}
            </div>
          </div>
        </div>

        {/* Quick prompts */}
        {!compact && !isActive && quickPrompts.length > 0 && (
          <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-border/50">
            <p className="text-[10px] sm:text-xs text-muted-foreground mb-2">Popular questions:</p>
            <div className="flex flex-wrap gap-1.5 sm:gap-2">
              {quickPrompts.map((prompt) => (
                <button
                  key={prompt}
                  onClick={toggleConversation}
                  disabled={isConnecting || isActive}
                  className="text-[10px] sm:text-xs px-2 sm:px-3 py-1 sm:py-1.5 rounded-full bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Active state indicator */}
        {isActive && (
          <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-border/50">
            <div className="flex items-center justify-center gap-2 text-xs text-accent">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-accent"></span>
              </span>
              <span>
                {assistantState === "speaking" ? "Dr. Maya is speaking..." : "Dr. Maya is listening..."}
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
});
